import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useStore} from 'react-redux';
import {renderRoutes} from 'react-router-config';
import RouterLoader from 'router/RouterLoader';
import {Route, useLocation} from 'react-router-dom';
import routes, {matchPathToRoute} from 'router/routes';
import {dataLoadFulfilled} from './router/router-actions';
import Arg from '@spothero/arg';
import {updateData as updateSearchRequest} from './search-request/search-request-actions';
import {Page} from './router/page';
import {clearSelectedSpot, selectSpot} from './spot/spot-actions';
import NotFound from './router/NotFound';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';

// Get state for next route/page via `getInitialState` and publish to reducers
// - handle edge cases with spot-details and route 'replace'
export async function reduceStateForRoute(
    prevLocation,
    location,
    dispatch,
    getState,
    setIsLoading
) {
    const {pathname: previousPath} = prevLocation;
    const {pathname, search, state: historyState = {}} = location;
    const route = matchPathToRoute(pathname);
    const previousRoute = matchPathToRoute(previousPath);

    if (isNil(route)) {
        throw new Error(`Unable to match path to route: ${pathname}`);
    }

    if (
        pathname.startsWith('/spot-details/') &&
        !includes(previousPath, 'airport') &&
        previousRoute.isSearch
    ) {
        const spotId = parseInt(pathname.split('spot-details/')[1], 10);

        dispatch(selectSpot(spotId, historyState));

        return;
    }

    const {component, params} = route;
    const isReplacedRoute = historyState.method === 'replace';

    if (
        previousPath.startsWith('/spot-details/') &&
        !includes(pathname, 'airport') &&
        route.isSearch
    ) {
        dispatch(clearSelectedSpot());
    } else if (!isReplacedRoute && isFunction(component.getInitialState)) {
        setIsLoading(true);

        const newState = await component.getInitialState({
            state: getState(),
            params,
            query: Arg.parse(search),
            route: {location},
        });

        dispatch(dataLoadFulfilled(newState));
    }

    // In the case where:
    // - we are replacing the URL
    // - and do not want to fetch new data
    // - but do need to change the page type
    // for checkout, this occurs when adding new URL query parameters
    if (isReplacedRoute && pathname.startsWith('/checkout/')) {
        dispatch(updateSearchRequest({pageType: Page.CHECKOUT}));
    }
}

// Consistently translate /spot-details -> /search
const getPath = ({pathname}) => {
    const {isSearch} = matchPathToRoute(pathname) || {};

    return pathname.startsWith('/spot-details/') ||
        (!includes(pathname, 'airport') && isSearch)
        ? '/search'
        : pathname;
};

function Navigator({extraProps}) {
    const dispatch = useDispatch();
    const store = useStore();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [path, setPath] = useState(getPath(location));
    const prevLocationRef = useRef(null);

    useEffect(() => {
        const prevLocation = prevLocationRef.current;

        prevLocationRef.current = location;

        if (prevLocation === null) return;

        if (
            location.pathname !== prevLocation.pathname ||
            location.search !== prevLocation.search ||
            !isEqual(location.state, prevLocation.state)
        ) {
            reduceStateForRoute(
                prevLocation,
                location,
                dispatch,
                store.getState,
                setIsLoading
            )
                .then(() => {
                    setPath(getPath(location));
                    setIsNotFound(false);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsNotFound(true);
                    setIsLoading(false);
                });
        }
    }, [location, dispatch, store]);

    return (
        <Route
            location={{pathname: path}}
            render={() =>
                isNotFound ? (
                    <NotFound />
                ) : isLoading ? (
                    <RouterLoader />
                ) : (
                    renderRoutes(routes, extraProps)
                )
            }
        />
    );
}

Navigator.propTypes = {
    extraProps: PropTypes.object.isRequired,
};

export default Navigator;
